import { tracked } from '@glimmer/tracking';
import cloneDeep from 'lodash.clonedeep';
import { TrackedObject } from '@ember-compat/tracked-built-ins';

import OutputRangeOption from '../general/output-range-option';

import Constants from '../../utils/constants';
import { GET_CDP_NUMERIC_VALUE, GET_CDP_VALUE, TEMPERATURE_RANGE } from '../../utils/formulas';
import Utils from '../../utils/utils';

export default class Dac {
  cdpParameters = null;

  @tracked type = null;
  @tracked data = null;
  @tracked gridData = null;
  @tracked selected = null;

  constructor(cdpParameters) {
    this.cdpParameters = cdpParameters;
  }

  get resolutionInBits() {
    return GET_CDP_NUMERIC_VALUE(Constants.CDP.PARAMETERS.RES, this.selected, this.cdpParameters).value;
  }

  get selectedVrefPosExternal() {
    const expectedMin = GET_CDP_NUMERIC_VALUE(
        Constants.CDP.PARAMETERS.VREF_POS_EXTERNAL.MIN,
        this.selected,
        this.cdpParameters,
      ),
      expectedMax = GET_CDP_NUMERIC_VALUE(
        Constants.CDP.PARAMETERS.VREF_POS_EXTERNAL.MAX,
        this.selected,
        this.cdpParameters,
      );
    return {
      min: expectedMin.value <= expectedMax.value ? expectedMin : expectedMax,
      max: expectedMin.value <= expectedMax.value ? expectedMax : expectedMin,
    };
  }

  get selectedVrefNegExternal() {
    const expectedMin = GET_CDP_NUMERIC_VALUE(
        Constants.CDP.PARAMETERS.VREF_NEG_EXTERNAL.MIN,
        this.selected,
        this.cdpParameters,
      ),
      expectedMax = GET_CDP_NUMERIC_VALUE(
        Constants.CDP.PARAMETERS.VREF_NEG_EXTERNAL.MAX,
        this.selected,
        this.cdpParameters,
      );
    return {
      min: expectedMin.value <= expectedMax.value ? expectedMin : expectedMax,
      max: expectedMin.value <= expectedMax.value ? expectedMax : expectedMin,
    };
  }

  get selectedVrefNegExternalOrVrefPosExternal() {
    const vrefNegExternalMin = this.selectedVrefNegExternal.min,
      vrefNegExternalMax = this.selectedVrefNegExternal.max,
      vrefPosExternalMin = this.selectedVrefPosExternal.min,
      vrefPosExternalMax = this.selectedVrefPosExternal.max;

    return {
      min: !(vrefNegExternalMin.value || vrefNegExternalMin.value === 0) ? vrefPosExternalMin : vrefNegExternalMin,
      max: !(vrefNegExternalMax.value || vrefNegExternalMax.value === 0) ? vrefPosExternalMax : vrefNegExternalMax,
    };
  }

  get selectedVrefLevelInternal() {
    return GET_CDP_VALUE(Constants.CDP.PARAMETERS.VREF_LEVEL_INTERNAL, this.selected, this.cdpParameters);
  }

  get selectedOutputRangeOptions() {
    let options = GET_CDP_VALUE(Constants.CDP.PARAMETERS.OUTPUT_RANGE_OPTIONS, this.selected).value || [],
      tueIoutMax = GET_CDP_VALUE(Constants.CDP.PARAMETERS.TUE_IOUT_MAX, this.selected, this.cdpParameters).value || [],
      tueIoutMaxMap = {};

    tueIoutMax.forEach((text) => {
      const splitResult = text.split('=');
      if (splitResult.length === 2) tueIoutMaxMap[splitResult[0].trim()] = Number(splitResult[1].trim());
    });

    return options.map((option) => {
      const outputRange = option.trim().replaceAll('−', '-'), // Make sure the negative sign is used
        tueIoutMax = outputRange in tueIoutMaxMap ? tueIoutMaxMap[outputRange] : NaN;
      return new OutputRangeOption(outputRange, tueIoutMax);
    });
  }

  get selectedTemperatureRange() {
    const paramKey = Constants.CDP.PARAMETERS.TEMPERATURE_RANGE;
    if (!(this.selected && paramKey in this.selected)) return { min: null, max: null };

    const tempRange = this.selected[paramKey];

    if (tempRange.value && tempRange.value.length) {
      return TEMPERATURE_RANGE(tempRange.value[0]);
    } else {
      return {
        min: null,
        max: null,
      };
    }
  }
  handleDacTypeChange(selectedType, gridData) {
    this.type = selectedType;
    const data = {};

    if (gridData && gridData.view && gridData.data) {
      this.gridData = gridData;

      let selectedDac = gridData.data.data.find((item) => item.name === selectedType.defaultsSelected.dac);
      this.selected = cloneDeep(selectedDac);

      if (!selectedDac)
        console.warn(
          `The default part (${selectedType.defaultsSelected.dac}) could not be found in the dac (${selectedType.name}) pst (${selectedType.pst.id}) grid data`,
        );

      this.handleSelectedDacChange(selectedType, data);

      Object.keys(selectedType.defaults).forEach((parameterName) => {
        data[parameterName] = selectedType.defaults[parameterName];
      });
    } else {
      console.error(
        `Grid data could not be found for dac (${selectedType.name}) pst (${
          selectedType.pst ? selectedType.pst.id : ''
        })`,
      );
    }
    this.data = new TrackedObject(data);
  }

  handleSelectedDacChange(dacType, dataMaybe) {
    dacType.defaultsSelected.computed.forEach((computedItem) => {
      let defaultValue = cloneDeep(Utils.getValue(this, `${computedItem.defaultLocation}`)),
        foundOverride = (dacType.defaultsSelected.computedOverrides || []).find(
          (overrideItem) => overrideItem.propertyName === computedItem.propertyName,
        );

      if (foundOverride) {
        defaultValue = foundOverride.overrideFn(defaultValue);
      }

      (dataMaybe ? dataMaybe : this.data)[computedItem.propertyName] = new TrackedObject(defaultValue);
    });
  }
}
